/* nplus-test-ride.js — <nplus-test-ride> element.
   Three-step test-ride wizard: model → ride type → details.
   Submits to HubSpot Forms API v3. No backend. Follows nplus-electrify-expo.js style.
   Spec: docs/specs/2026-07-06-test-ride-page-design.md */
(function () {
  'use strict';

  var EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
  var ZIP_RE = /^\d{5}$/;
  var STEPS = ['model', 'ride_type', 'details'];
  // Below 3s is near-certainly a bot; kept low because tripping it silently drops the lead.
  var MIN_ELAPSED_MS = 3000;
  // Error keys whose invalid state lands on a specific control (others are radio groups).
  var ERROR_TARGETS = {
    zip: '[data-tr-zip]',
    consent: '[data-tr-consent]'
  };

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function getCookie(name) {
    var m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : '';
  }

  class NplusTestRide extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;

      var cfgEl = this.querySelector('.nplus-tr__config');
      try { this.cfg = JSON.parse(cfgEl ? cfgEl.textContent : '{}'); } catch (e) { this.cfg = {}; }
      var prodEl = this.querySelector('[data-tr-products]');
      try { this.products = JSON.parse(prodEl ? prodEl.textContent : '[]'); } catch (e) { this.products = []; }

      // The Gorgias chat widget is storewide (app embed); on this campaign page it's
      // hidden via CSS (theme.liquid) and removed here so it can't steal focus/taps.
      if (this.cfg.templateSuffix === 'test-rides-poc') this.hideGorgiasWidget();

      this.state = {
        step: 0,
        modelChoice: '', productId: '', variantId: '', rideType: '', zip: '',
        rideTiming: '', firstName: '', lastName: '', email: '', phone: '', consent: false
      };
      this.startedAt = Date.now();
      this.utm = {};
      var qs = new URLSearchParams(location.search);
      ['utm_source','utm_medium','utm_campaign','utm_content','utm_term'].forEach(function(k){ var v = qs.get(k); if (v) this.utm[k] = v; }, this);

      // Element cache
      this.panels = {};
      var self = this;
      Array.prototype.forEach.call(this.querySelectorAll('[data-tr-step]'), function (p) {
        self.panels[p.getAttribute('data-tr-step')] = p;
      });
      this.segs = this.querySelectorAll('[data-tr-seg]');
      this.stepLabelEl = this.querySelector('[data-tr-steplabel]');
      this.announceEl = this.querySelector('[data-tr-announce]');
      this.nextBtn = this.querySelector('[data-tr-next]');
      this.backBtn = this.querySelector('[data-tr-back]');
      this.zipEl = this.querySelector('[data-tr-zip]');
      this.zipWrapEl = this.querySelector('[data-tr-zip-wrap]');
      this.virtualNoteEl = this.querySelector('[data-tr-virtual-note]');
      this.consentEl = this.querySelector('[data-tr-consent]');
      this.statusEl = this.querySelector('[data-tr-status]');
      this.hpEl = this.querySelector('[data-tr-hp]');

      var consentLabel = this.querySelector('[data-tr-consent-label]');
      if (consentLabel) consentLabel.textContent = this.cfg.consentLabel || 'I consent.';

      // Events
      Array.prototype.forEach.call(this.querySelectorAll('[data-tr-model]'), function (r) {
        r.addEventListener('change', self.onModelChange.bind(self));
      });
      Array.prototype.forEach.call(this.querySelectorAll('[data-tr-ride-type]'), function (r) {
        r.addEventListener('change', self.onRideTypeChange.bind(self));
      });
      if (this.nextBtn) this.nextBtn.addEventListener('click', this.onNext.bind(this));
      if (this.backBtn) this.backBtn.addEventListener('click', function () { self.goTo(self.state.step - 1); });
      Array.prototype.forEach.call(this.segs, function (seg, i) {
        seg.addEventListener('click', function () {
          if (i >= self.state.step) return;
          self.goTo(i);
        });
      });

      // Deep link: ?model=<handle> (or ?model=undecided) preselects and jumps to the ride-type step.
      var wanted = new URLSearchParams(location.search).get('model');
      var start = 0;
      if (wanted === 'undecided') {
        var undecidedRadio = this.querySelector('[data-tr-model][value="undecided"]');
        if (undecidedRadio) {
          undecidedRadio.checked = true;
          this.onModelChange();
          if (this.state.modelChoice) start = 1;
        }
      } else if (wanted) {
        for (var i = 0; i < this.products.length; i++) {
          if (this.products[i].handle === wanted) {
            var radio = this.querySelector('[data-tr-model][value="' + this.products[i].id + '"]');
            if (radio) {
              radio.checked = true;
              this.onModelChange();
              if (this.state.modelChoice) start = 1;
            }
            break;
          }
        }
      }
      this.goTo(start, { silent: true });
    }

    t(key, fallback) { return (this.cfg && this.cfg.i18n && this.cfg.i18n[key]) || fallback; }

    /* ---------- navigation ---------- */

    goTo(i, opts) {
      opts = opts || {};
      if (i < 0 || i >= STEPS.length) return;
      this.state.step = i;
      var self = this;
      STEPS.forEach(function (name, idx) {
        if (self.panels[name]) self.panels[name].hidden = idx !== i;
      });
      Array.prototype.forEach.call(this.segs, function (seg, idx) {
        seg.classList.toggle('is-done', idx < i);
        seg.classList.toggle('is-current', idx === i);
        seg.disabled = idx >= i; // only completed steps are clickable (backwards-only nav)
        if (idx === i) seg.setAttribute('aria-current', 'step');
        else seg.removeAttribute('aria-current');
      });
      var label = this.t('step_of', 'Step %1 of %2').replace('%1', i + 1).replace('%2', STEPS.length) +
        ' · ' + (this.t('step_labels', [])[i] || '');
      if (this.stepLabelEl) this.stepLabelEl.textContent = label;
      if (this.announceEl && !opts.silent) this.announceEl.textContent = label;
      if (this.backBtn) this.backBtn.hidden = i === 0;
      if (this.nextBtn) this.nextBtn.textContent = i === STEPS.length - 1 ? this.t('submit', 'Submit request') : this.t('continue', 'Continue');
      if (!opts.silent) {
        var heading = this.panels[STEPS[i]] && this.panels[STEPS[i]].querySelector('[data-tr-heading]');
        if (heading) heading.focus();
      }
    }

    advance() {
      this.goTo(this.state.step + 1);
    }

    onNext() {
      this.clearErrors();
      var name = STEPS[this.state.step];
      if (!this.validateStep(name)) return;
      if (this.state.step === STEPS.length - 1) { this.onSubmit(); return; }
      this.advance();
    }

    /* ---------- step 1: model ---------- */

    onModelChange() {
      this.hideError('model');
      var checked = this.querySelector('[data-tr-model]:checked');
      var value = checked ? checked.value : '';
      this.state.modelChoice = value;
      // "undecided" isn't a real product — no product/variant to reference.
      this.state.productId = (value && value !== 'undecided') ? value : '';
      this.state.variantId = '';
    }

    findProduct(id) {
      for (var i = 0; i < this.products.length; i++) {
        if (String(this.products[i].id) === String(id)) return this.products[i];
      }
      return null;
    }

    /* ---------- step 2: ride type ---------- */

    onRideTypeChange() {
      this.hideError('ride_type');
      var checked = this.querySelector('[data-tr-ride-type]:checked');
      this.state.rideType = checked ? checked.value : '';
      // Selecting either card clears the other's state and any ZIP error.
      this.state.zip = '';
      if (this.zipEl) this.zipEl.value = '';
      this.hideError('zip');
      this.updateRideTypeUI();
    }

    updateRideTypeUI() {
      var isDealer = this.state.rideType === 'dealer';
      if (this.zipWrapEl) this.zipWrapEl.hidden = !isDealer;
      if (this.virtualNoteEl) this.virtualNoteEl.hidden = this.state.rideType !== 'virtual';
    }

    /* ---------- validation ---------- */

    errorTarget(key) {
      return this.querySelector(ERROR_TARGETS[key] || '[data-tr-field="' + key + '"]');
    }
    showError(key, msg) {
      var el = this.querySelector('[data-tr-error-for="' + key + '"]');
      if (el) { el.textContent = msg || this.t('required', 'Required'); el.hidden = false; }
      var target = this.errorTarget(key);
      if (target) target.setAttribute('aria-invalid', 'true');
    }
    hideError(key) {
      var el = this.querySelector('[data-tr-error-for="' + key + '"]');
      if (el) el.hidden = true;
      var target = this.errorTarget(key);
      if (target) target.removeAttribute('aria-invalid');
    }
    clearErrors() {
      Array.prototype.forEach.call(this.querySelectorAll('[data-tr-error-for]'), function (e) { e.hidden = true; });
      Array.prototype.forEach.call(this.querySelectorAll('[aria-invalid]'), function (e) { e.removeAttribute('aria-invalid'); });
      if (this.statusEl) this.statusEl.hidden = true;
    }

    validateStep(name) {
      var s = this.state, ok = true, self = this;
      var fail = function (key, msg) { ok = false; self.showError(key, msg); };
      if (name === 'model') {
        if (!s.modelChoice) fail('model', this.t('select_model', 'Select a model to continue'));
      } else if (name === 'ride_type') {
        if (!s.rideType) {
          fail('ride_type', this.t('select_ride_type', "Choose how you'd like to ride."));
        } else if (s.rideType === 'dealer') {
          var zip = this.zipEl ? this.zipEl.value.trim() : '';
          if (!ZIP_RE.test(zip)) fail('zip', this.t('invalid_zip', 'Please enter a 5 digit ZIP code.'));
          else s.zip = zip;
        }
      } else if (name === 'details') {
        var timing = this.querySelector('[data-tr-ride-timing]:checked');
        s.rideTiming = timing ? timing.value : '';
        if (!s.rideTiming) fail('ride_timing', this.t('select_ride_timing', "Let us know roughly when you'd like to ride."));

        var val = function (k) { var el = self.querySelector('[data-tr-field="' + k + '"]'); return el ? el.value.trim() : ''; };
        s.firstName = val('firstName'); s.lastName = val('lastName');
        s.email = val('email'); s.phone = val('phone');
        s.consent = !!(this.consentEl && this.consentEl.checked);
        if (!s.firstName) fail('firstName');
        if (!s.lastName) fail('lastName');
        if (!s.email) fail('email');
        else if (!EMAIL_RE.test(s.email)) fail('email', this.t('invalid_email', 'Enter a valid email'));
        if (!s.phone) fail('phone', this.t('enter_phone', 'Enter a phone number'));
        if (!s.consent) fail('consent', this.t('consent_required', 'Consent is required to submit.'));
      }
      return ok;
    }

    /* ---------- submit ---------- */

    setStatus(msg, isError) {
      if (!this.statusEl) return;
      this.statusEl.textContent = msg || '';
      this.statusEl.hidden = !msg;
      this.statusEl.classList.toggle('nplus-bb__status--error', !!isError);
    }
    setBusy(b) { if (this.nextBtn) { this.nextBtn.disabled = b; this.nextBtn.setAttribute('aria-busy', b ? 'true' : 'false'); } }

    onSubmit() {
      this.setStatus('');
      if (!this.cfg.formGuid || !this.cfg.portalId) { this.setStatus(this.t('misconfigured', 'This form is not available right now. Please try again later.'), true); return; }
      // Spam guards: honeypot filled or submitted implausibly fast → silently drop.
      if ((this.hpEl && this.hpEl.value) || (Date.now() - this.startedAt < MIN_ELAPSED_MS)) { this.showSuccess(); return; }

      var s = this.state;
      var fields = [
        { name: 'firstname', value: s.firstName },
        { name: 'lastname', value: s.lastName },
        { name: 'email', value: s.email },
        { name: 'test_ride_model', value: s.modelChoice === 'undecided' ? 'Not sure yet' : (this.findProduct(s.productId) ? this.findProduct(s.productId).title : '') },
        { name: 'ride_type', value: s.rideType },
        { name: 'zip', value: s.zip },
        { name: 'ride_timing', value: s.rideTiming }
      ];
      fields.push({ name: 'phone', value: s.phone });
      if (this.cfg.sourceTag) fields.push({ name: 'test_ride_source', value: this.cfg.sourceTag });
      Object.keys(this.utm).forEach(function(k){ fields.push({ name: k, value: this.utm[k] }); }, this);

      var payload = {
        fields: fields,
        context: { pageUri: location.href, pageName: this.cfg.pageName || document.title },
        // NOTE: key is consentToProcess (HubSpot v3 schema) — `consented` is silently invalid.
        legalConsentOptions: { consent: { consentToProcess: true, text: this.cfg.consentLabel || '' } }
      };
      var hutk = getCookie('hubspotutk');
      if (hutk) payload.context.hutk = hutk;

      var self = this;
      this.setBusy(true);
      fetch('https://api.hsforms.com/submissions/v3/integration/submit/' +
        encodeURIComponent(this.cfg.portalId) + '/' + encodeURIComponent(this.cfg.formGuid), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })
        .then(function (r) {
          if (!r.ok) return r.json().then(function (body) {
            throw new Error((body && body.message) || 'submit failed');
          }).catch(function (e) { throw (e instanceof Error ? e : new Error('submit failed')); });
          self.showSuccess();
        })
        .catch(function (e) {
          console.error('[nplus-test-ride] submit failed:', e);
          self.setStatus(self.t('submit_error', 'Could not submit your request. Please try again.'), true);
          self.setBusy(false);
        });
    }

    showSuccess() {
      if (this.cfg.successUrl) { window.location.assign(this.cfg.successUrl); return; }
      var title = this.t('success_title', 'Request received');
      var body = this.t('success_body', 'Thanks — your test ride request is in.');
      var inner = this.querySelector('.nplus-tr__inner');
      var logo = inner.querySelector('.nplus-tr__logo'); // keep the brand mark on the success screen
      inner.innerHTML = '';
      if (logo) inner.appendChild(logo);
      var div = document.createElement('div');
      div.className = 'nplus-tr__success';
      div.innerHTML = '<h2 tabindex="-1">' + esc(title) + '</h2><p>' + esc(body) + '</p>';
      var back = document.createElement('a');
      back.className = 'nplus-tr__back';
      back.href = '/';
      back.textContent = this.t('back_home', 'Go back');
      div.appendChild(back);
      inner.appendChild(div);
      var h = div.querySelector('h2');
      if (h) h.focus();
    }

    // Gorgias can inject its container well after page load — poll via MutationObserver
    // rather than a one-shot check, and stop watching once it's gone.
    hideGorgiasWidget() {
      var remove = function () {
        var el = document.getElementById('gorgias-chat-container');
        if (!el) return false;
        el.parentNode.removeChild(el);
        return true;
      };
      if (remove()) return;
      var observer = new MutationObserver(function () {
        if (remove()) observer.disconnect();
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  if (!customElements.get('nplus-test-ride')) {
    customElements.define('nplus-test-ride', NplusTestRide);
  }
})();
